import { DEFAULT_HANDLING, type InputState, type RacerState, speed as racerSpeed } from './Physics';
import { FIXED_UPDATE_DT } from './FrameTiming';
import { fromRacer, simStep, trackMarginAt, wallMarginAt } from './AutopilotSim';
import {
  getAutopilotLine,
  sampleAutopilotLine,
  sampleAutopilotLineAhead,
  type AutopilotLine,
} from './AutopilotLine';
import { locateTrackPosition, type Track } from '../track/TrackLoader';

export interface AutopilotParams {
  lateralAcceleration: number;
  minSpeed: number;
  maxSpeedMultiplier: number;
  targetMargin: number;
  laneBias: number;
  laneErrorWeight: number;
  criticalMargin: number;
  steeringGain: number;
  headingGain: number;
  crossTrackGain: number;
  throttleMargin: number;
  brakeMargin: number;
  rolloutFrames: number;
  rolloutDt: number;
  progressWeight: number;
  marginWeight: number;
  speedWeight: number;
  offTrackPenalty: number;
  baseActionBonus: number;
  stuckSpeed: number;
  stuckConfirmSeconds: number;
  recoverySeconds: number;
  cooldownSeconds: number;
  telemetryMaxFrames: number;
  telemetryMaxLaps: number;
}

export type AutopilotMode = 'normal' | 'stuck' | 'recovering' | 'rejoining' | 'cooldown';

export interface AutopilotTelemetryFrame {
  step: number;
  arc: number;
  x: number;
  z: number;
  vx: number;
  vz: number;
  heading: number;
  input: {
    throttle: number;
    brake: number;
    steer: number;
    leftThrust?: number;
    rightThrust?: number;
  };
  mode: AutopilotMode;
}

export interface AutopilotTelemetryLap {
  lapTimeMs: number;
  frames: AutopilotTelemetryFrame[];
}

export interface AutopilotState {
  lastSteerDirection: -1 | 0 | 1;
  params: AutopilotParams;
  mode: AutopilotMode;
  modeEnteredStep: number;
  lowSpeedStreakSteps: number;
  stepCounter: number;
  lastSafetyOverride: boolean;
  telemetry: {
    active: boolean;
    lapStartedStep: number;
    frames: AutopilotTelemetryFrame[];
    laps: AutopilotTelemetryLap[];
  };
}

export interface AutopilotStepContext {
  step?: number;
  dt?: number;
}

export const DEFAULT_AUTOPILOT_PARAMS: AutopilotParams = {
  lateralAcceleration: 82,
  minSpeed: 17,
  maxSpeedMultiplier: 0.56,
  targetMargin: 4,
  laneBias: 0,
  laneErrorWeight: 34,
  criticalMargin: 0.8,
  steeringGain: 1.45,
  headingGain: 0.55,
  crossTrackGain: 1.05,
  throttleMargin: 1.8,
  brakeMargin: 2.5,
  rolloutFrames: 5,
  rolloutDt: 0.06,
  progressWeight: 3,
  marginWeight: 5,
  speedWeight: 0.1,
  offTrackPenalty: 8000,
  baseActionBonus: 80,
  stuckSpeed: 3,
  stuckConfirmSeconds: 1.2,
  recoverySeconds: 1.2,
  cooldownSeconds: 0.45,
  telemetryMaxFrames: Math.ceil(30 / FIXED_UPDATE_DT),
  telemetryMaxLaps: 10,
};

export const AGGRESSIVE_AUTOPILOT_PARAMS: AutopilotParams = {
  ...DEFAULT_AUTOPILOT_PARAMS,
  lateralAcceleration: 145,
  minSpeed: 24,
  maxSpeedMultiplier: 0.82,
  targetMargin: 2.2,
  laneBias: 0.25,
  laneErrorWeight: 32,
  crossTrackGain: 0.55,
  baseActionBonus: 110,
};

export const TIMID_AUTOPILOT_PARAMS: AutopilotParams = {
  ...DEFAULT_AUTOPILOT_PARAMS,
  lateralAcceleration: 85,
  minSpeed: 16,
  maxSpeedMultiplier: 0.52,
  targetMargin: 4.5,
  laneBias: 0,
  laneErrorWeight: 60,
  crossTrackGain: 0.95,
  baseActionBonus: 70,
};

const ZERO_INPUT: InputState = { throttle: 0, brake: 0, steer: 0, boost: 0 };

export function createAutopilotState(params: Partial<AutopilotParams> = {}): AutopilotState {
  return {
    lastSteerDirection: 0,
    params: { ...DEFAULT_AUTOPILOT_PARAMS, ...params },
    mode: 'normal',
    modeEnteredStep: 0,
    lowSpeedStreakSteps: 0,
    stepCounter: 0,
    lastSafetyOverride: false,
    telemetry: {
      active: false,
      lapStartedStep: 0,
      frames: [],
      laps: [],
    },
  };
}

export function computeAutopilotInput(
  track: Track,
  state: RacerState,
  controller: AutopilotState,
  context: AutopilotStepContext = {},
): InputState {
  const step = context.step ?? controller.stepCounter + 1;
  const dt = context.dt ?? FIXED_UPDATE_DT;
  controller.stepCounter = step;

  const params = controller.params;
  const line = getAutopilotLine(track, {
    lateralAcceleration: params.lateralAcceleration,
    minSpeed: params.minSpeed,
    maxSpeed: DEFAULT_HANDLING.maxSpeed * params.maxSpeedMultiplier,
    laneBias: params.laneBias,
    safetyMargin: params.targetMargin,
  });
  const pos = locateTrackPosition(track, state.position.x, state.position.z);
  const trackMargin = trackLimitMargin(pos.signedLateral, pos.widthLeft, pos.widthRight);
  const v = racerSpeed(state);
  const tangentSpeed = state.velocity.x * pos.tx + state.velocity.z * pos.tz;
  const forwardDot = Math.sin(state.heading) * pos.tx + Math.cos(state.heading) * pos.tz;

  const recoveryInput = updateRecoveryMode(track, line, state, controller, pos.arcLength, trackMargin, v, step, dt);
  if (recoveryInput) {
    recordAutopilotFrame(track, state, controller, recoveryInput, step);
    return recoveryInput;
  }

  if (v > params.stuckSpeed && (tangentSpeed < -2 || forwardDot < -0.25)) {
    const steer = clamp(localBearingToVector(state, pos.tx, pos.tz) * 2, -1, 1);
    const inputState = tangentSpeed < -2 && v > 7
      ? { throttle: 0, brake: 1, steer: 0, boost: 0 }
      : { throttle: 1, brake: 0, steer, boost: 0 };
    controller.lastSafetyOverride = true;
    recordAutopilotFrame(track, state, controller, inputState, step);
    return inputState;
  }

  const baseInput = lineTrackingInput(line, state, pos.arcLength, pos.signedLateral, trackMargin, v, params);
  const safeInput = shouldRunSafety(track, state, trackMargin, params)
    ? chooseSafeInput(track, line, state, baseInput, params)
    : baseInput;

  controller.lastSafetyOverride = !sameInput(baseInput, safeInput);
  updateStuckDetector(controller, baseInput, trackMargin, v, step, dt);
  recordAutopilotFrame(track, state, controller, safeInput, step);
  return safeInput;
}

export function startAutopilotLap(controller: AutopilotState, step = controller.stepCounter): void {
  controller.telemetry.active = true;
  controller.telemetry.lapStartedStep = step;
  controller.telemetry.frames = [];
}

export function completeAutopilotLap(controller: AutopilotState, lapTimeMs: number, step = controller.stepCounter): void {
  if (!controller.telemetry.active || controller.telemetry.frames.length === 0) {
    startAutopilotLap(controller, step);
    return;
  }
  controller.telemetry.laps.push({
    lapTimeMs,
    frames: controller.telemetry.frames.slice(),
  });
  while (controller.telemetry.laps.length > controller.params.telemetryMaxLaps) {
    controller.telemetry.laps.shift();
  }
  startAutopilotLap(controller, step);
}

export function resetAutopilotRun(controller: AutopilotState): void {
  controller.mode = 'normal';
  controller.modeEnteredStep = controller.stepCounter;
  controller.lowSpeedStreakSteps = 0;
  controller.lastSteerDirection = 0;
  controller.lastSafetyOverride = false;
  controller.telemetry.active = false;
  controller.telemetry.frames = [];
}

export function getAutopilotLaps(controller: AutopilotState): AutopilotTelemetryLap[] {
  return controller.telemetry.laps;
}

function lineTrackingInput(
  line: AutopilotLine,
  state: RacerState,
  arcLength: number,
  signedLateral: number,
  trackMargin: number,
  speed: number,
  params: AutopilotParams,
): InputState {
  const here = sampleAutopilotLine(line, arcLength);
  const lookahead = trackMargin < 1.5 ? Math.max(8, speed * 0.35) : here.lookahead;
  const target = sampleAutopilotLineAhead(line, arcLength, lookahead);

  const targetBearing = localBearingToPoint(state, target.x, target.z);
  const headingError = localBearingToVector(state, target.tx, target.tz);
  const laneError = signedLateral - here.desiredLateralOffset;
  const laneCorrection = Math.atan2(params.crossTrackGain * laneError, speed + 6);
  const steer = clamp(
    targetBearing * params.steeringGain
      + headingError * params.headingGain
      + laneCorrection,
    -1,
    1,
  );

  const laneSpeedPenalty = Math.max(0, Math.abs(laneError) - 1.25) * 1.5;
  const targetSpeed = trackMargin < 0
    ? Math.min(14, here.targetSpeed)
    : Math.max(params.minSpeed, here.targetSpeed - laneSpeedPenalty);
  const speedError = targetSpeed - speed;
  const brake = speedError < -params.brakeMargin && speed > 4 ? 1 : 0;
  const throttle = brake ? 0 : speedError > params.throttleMargin || speed < params.minSpeed ? 1 : 0;

  return { throttle, brake, steer, boost: 0 };
}

function updateRecoveryMode(
  track: Track,
  line: AutopilotLine,
  state: RacerState,
  controller: AutopilotState,
  arcLength: number,
  trackMargin: number,
  speed: number,
  step: number,
  dt: number,
): InputState | null {
  const elapsedSeconds = (step - controller.modeEnteredStep) * dt;

  if (controller.mode === 'normal' && trackMargin < -0.75) {
    controller.mode = 'rejoining';
    controller.modeEnteredStep = step;
  }

  if (controller.mode === 'stuck') {
    controller.mode = 'recovering';
    controller.modeEnteredStep = step;
    return recoveryInput(locateTrackPosition(track, state.position.x, state.position.z).signedLateral);
  }

  if (controller.mode === 'recovering') {
    if (elapsedSeconds >= controller.params.recoverySeconds) {
      controller.mode = 'cooldown';
      controller.modeEnteredStep = step;
      return { ...ZERO_INPUT };
    }
    return recoveryInput(locateTrackPosition(track, state.position.x, state.position.z).signedLateral);
  }

  if (controller.mode === 'rejoining') {
    if (trackMargin > 1.5) {
      controller.mode = 'cooldown';
      controller.modeEnteredStep = step;
      return { ...ZERO_INPUT };
    }
    const currentPos = locateTrackPosition(track, state.position.x, state.position.z);
    if (trackMargin < 0.5) {
      const steer = clamp(localBearingToPoint(state, currentPos.closestX, currentPos.closestZ) * 2.5, -1, 1);
      return {
        throttle: speed < 12 ? 1 : 0,
        brake: speed > 16 ? 1 : 0,
        steer,
        boost: 0,
      };
    }
    const input = lineTrackingInput(line, state, arcLength, currentPos.signedLateral, trackMargin, speed, {
      ...controller.params,
      minSpeed: Math.min(controller.params.minSpeed, 12),
      throttleMargin: 0.5,
      brakeMargin: 999,
    });
    return { ...input, throttle: speed < 16 ? 1 : input.throttle, brake: 0 };
  }

  if (controller.mode === 'cooldown' && elapsedSeconds >= controller.params.cooldownSeconds) {
    controller.mode = 'normal';
    controller.modeEnteredStep = step;
    controller.lowSpeedStreakSteps = 0;
  }

  return null;
}

function chooseSafeInput(
  track: Track,
  line: AutopilotLine,
  state: RacerState,
  baseInput: InputState,
  params: AutopilotParams,
): InputState {
  const startArc = locateTrackPosition(track, state.position.x, state.position.z).arcLength;
  const candidates = candidateInputs(baseInput)
    .filter((candidate) => baseInput.throttle > 0 || candidate === baseInput || candidate.throttle <= 0);
  let best = baseInput;
  let bestScore = -Infinity;
  let baseScore = -Infinity;
  for (const candidate of candidates) {
    const isBase = candidate === baseInput;
    const score = scoreRollout(track, line, state, startArc, candidate, params, isBase);
    if (isBase) baseScore = score;
    if (score > bestScore) {
      best = candidate;
      bestScore = score;
    }
  }
  return bestScore > baseScore + 0.001 ? best : baseInput;
}

function shouldRunSafety(track: Track, state: RacerState, trackMargin: number, params: AutopilotParams): boolean {
  const emergencyMargin = Math.min(params.targetMargin, 3.5);
  if (trackMargin < emergencyMargin) return true;
  const projectedX = state.position.x + state.velocity.x * 0.35;
  const projectedZ = state.position.z + state.velocity.z * 0.35;
  return trackMarginAt(track, projectedX, projectedZ) < emergencyMargin;
}

function scoreRollout(
  track: Track,
  line: AutopilotLine,
  state: RacerState,
  startArc: number,
  input: InputState,
  params: AutopilotParams,
  isBase: boolean,
): number {
  let sim = fromRacer(state);
  let minWallMargin = wallMarginAt(track, sim.position.x, sim.position.z);
  let minTrackMargin = trackMarginAt(track, sim.position.x, sim.position.z);
  let maxLaneError = laneErrorAt(track, line, startArc, sim.position.x, sim.position.z);
  let offTrack = minTrackMargin < 0;
  let endArc = startArc;
  let endSpeed = racerSpeed(sim);

  for (let frame = 0; frame < params.rolloutFrames; frame++) {
    const result = simStep(track, sim, input, params.rolloutDt);
    sim = result.state;
    minWallMargin = Math.min(minWallMargin, result.wallMargin);
    minTrackMargin = Math.min(minTrackMargin, result.trackMargin);
    maxLaneError = Math.max(maxLaneError, laneErrorAt(track, line, result.arcLength, sim.position.x, sim.position.z));
    offTrack = offTrack || result.trackMargin < 0 || !result.insideWalls;
    endArc = result.arcLength;
    endSpeed = result.speed;
  }

  let signedProgress = endArc - startArc;
  if (signedProgress < -track.totalLength * 0.5) signedProgress += track.totalLength;
  else if (signedProgress > track.totalLength * 0.5) signedProgress -= track.totalLength;
  const reverseProgress = Math.max(0, -signedProgress);
  const progress = Math.max(0, signedProgress);

  const marginDeficit = Math.max(0, params.targetMargin - minWallMargin);
  const criticalDeficit = Math.max(0, params.criticalMargin - minWallMargin);
  const trackDeficit = Math.max(0, 1 - minTrackMargin);
  const trackComfortDeficit = Math.max(0, params.targetMargin - minTrackMargin);
  const laneDeficit = Math.max(0, maxLaneError - 1.5);
  return progress * params.progressWeight
    + minWallMargin * params.marginWeight
    + minTrackMargin * params.marginWeight
    + endSpeed * params.speedWeight
    - reverseProgress * params.progressWeight * 12
    - marginDeficit * marginDeficit * 5
    - criticalDeficit * criticalDeficit * 30
    - trackDeficit * trackDeficit * 120
    - trackComfortDeficit * trackComfortDeficit * 18
    - laneDeficit * laneDeficit * params.laneErrorWeight
    - (offTrack ? params.offTrackPenalty : 0)
    + (isBase ? params.baseActionBonus : 0);
}

function laneErrorAt(track: Track, line: AutopilotLine, arcLength: number, x: number, z: number): number {
  const pos = locateTrackPosition(track, x, z);
  const sample = sampleAutopilotLine(line, arcLength);
  return Math.abs(pos.signedLateral - sample.desiredLateralOffset);
}

function updateStuckDetector(
  controller: AutopilotState,
  inputState: InputState,
  trackMargin: number,
  speed: number,
  step: number,
  dt: number,
): void {
  if (controller.mode !== 'normal') return;
  const throttleCommanded = inputState.throttle > 0.5 || (inputState.leftThrust ?? 0) > 0.5 || (inputState.rightThrust ?? 0) > 0.5;
  const constrained = speed < controller.params.stuckSpeed && throttleCommanded && trackMargin < controller.params.targetMargin;
  controller.lowSpeedStreakSteps = constrained ? controller.lowSpeedStreakSteps + 1 : 0;
  const confirmSteps = Math.ceil(controller.params.stuckConfirmSeconds / Math.max(1e-6, dt));
  if (controller.lowSpeedStreakSteps >= confirmSteps) {
    controller.mode = 'stuck';
    controller.modeEnteredStep = step;
  }
}

function recoveryInput(signedLateral: number): InputState {
  const yawBias = Math.sign(signedLateral || 1) * 0.25;
  return {
    throttle: 0,
    brake: 0,
    steer: 0,
    leftThrust: clamp(-0.7 - yawBias, -1, 0),
    rightThrust: clamp(-0.7 + yawBias, -1, 0),
    boost: 0,
  };
}

function candidateInputs(baseInput: InputState): InputState[] {
  const candidates = [
    baseInput,
    input(1, 0, 0),
    input(1, 0, 1),
    input(1, 0, -1),
    input(0, 1, 0),
    input(0, 1, 1),
    input(0, 1, -1),
  ];
  const seen = new Set<string>();
  return candidates.filter((candidate) => {
    const key = `${candidate.throttle}:${candidate.brake}:${candidate.steer}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function recordAutopilotFrame(
  track: Track,
  state: RacerState,
  controller: AutopilotState,
  inputState: InputState,
  step: number,
): void {
  if (!controller.telemetry.active) return;
  const pos = locateTrackPosition(track, state.position.x, state.position.z);
  controller.telemetry.frames.push({
    step,
    arc: pos.arcLength,
    x: state.position.x,
    z: state.position.z,
    vx: state.velocity.x,
    vz: state.velocity.z,
    heading: state.heading,
    input: {
      throttle: inputState.throttle,
      brake: inputState.brake,
      steer: inputState.steer,
      leftThrust: inputState.leftThrust,
      rightThrust: inputState.rightThrust,
    },
    mode: controller.mode,
  });
  const overflow = controller.telemetry.frames.length - controller.params.telemetryMaxFrames;
  if (overflow > 0) controller.telemetry.frames.splice(0, overflow);
}

function localBearingToPoint(state: RacerState, x: number, z: number): number {
  const dx = x - state.position.x;
  const dz = z - state.position.z;
  return localBearingToVector(state, dx, dz);
}

function localBearingToVector(state: RacerState, x: number, z: number): number {
  const forwardX = Math.sin(state.heading);
  const forwardZ = Math.cos(state.heading);
  const rightX = Math.cos(state.heading);
  const rightZ = -Math.sin(state.heading);
  const localForward = x * forwardX + z * forwardZ;
  const localRight = x * rightX + z * rightZ;
  return Math.atan2(localRight, localForward);
}

function trackLimitMargin(signedLateral: number, widthLeft: number, widthRight: number): number {
  return signedLateral >= 0
    ? widthLeft - signedLateral
    : widthRight + signedLateral;
}

function input(throttle: 0 | 1, brake: 0 | 1, steer: number): InputState {
  return { throttle, brake, steer: clamp(steer, -1, 1), boost: 0 };
}

function sameInput(a: InputState, b: InputState): boolean {
  return a.throttle === b.throttle
    && a.brake === b.brake
    && a.steer === b.steer
    && a.leftThrust === b.leftThrust
    && a.rightThrust === b.rightThrust;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
